/**
 * Optimistic message sends. The composer registers a send here the moment the
 * user hits Enter; lists render it immediately (greyed out) while the queued
 * work encrypts and posts it, then drop it once the server copy (matched by
 * `clientMessageId`) is in the buffer. Failed sends stay until retried or
 * discarded.
 *
 * Attachment sends that go through the media outbox are not queued here; the
 * outbox persists them and {@link outboxJobToPendingSend} turns its jobs into
 * the same row shape.
 *
 * Sends run one at a time per scope (a conversation or Space channel, threads
 * included) so they land in the order they were typed. Retries reuse the same
 * `clientMessageId`; both send APIs de-duplicate on it, so a retry after a lost
 * response cannot post twice.
 */

import type { MediaOutboxStage } from './mediaOutbox/mediaOutboxTypes';
import { revokePendingPreviews, type PendingAttachmentPreview } from './pendingSendPreviews';

export type PendingSendStatus = 'sending' | 'failed';

export interface PendingSend {
  clientMessageId: string;
  /** {@link conversationSendScope} or {@link spaceChannelSendScope}. */
  scope: string;
  fromIdentityId: string;
  /** Serialized composer payload (what the recipient will decrypt). */
  plaintext: string;
  createdAt: string;
  /** Thread the reply belongs to, if sent from a thread panel. */
  threadRootId?: string;
  /** Shown in the main stream: not a thread-only reply. */
  inStream: boolean;
  replyToMessageId?: string;
  status: PendingSendStatus;
  /** User-facing reason, when the failure has one. */
  error?: string;
  /** Local previews of attachments being sent. */
  previews?: readonly PendingAttachmentPreview[];
  /** Set when the media outbox owns this send (retry/cancel go through it). */
  outboxJobId?: string;
  outboxStage?: MediaOutboxStage;
}

/** Client-only send state carried by a not-yet-delivered list row. */
export interface LocalPendingSend {
  clientMessageId: string;
  status: PendingSendStatus;
  error?: string;
  previews?: readonly PendingAttachmentPreview[];
  outboxJobId?: string;
  outboxStage?: MediaOutboxStage;
}

export type PendingSendOutcome = { ok: true } | { ok: false; error?: string };

type Run = () => Promise<PendingSendOutcome>;

export function conversationSendScope(conversationId: string): string {
  return `conversation:${conversationId}`;
}

export function spaceChannelSendScope(channelId: string): string {
  return `space-channel:${channelId}`;
}

/** List row id for a pending send; never sent to the server. */
export function pendingSendRowId(clientMessageId: string): string {
  return `pending:${clientMessageId}`;
}

let entries: readonly PendingSend[] = [];
const runs = new Map<string, Run>();
const queues = new Map<string, Promise<void>>();
const listeners = new Set<() => void>();

function commit(next: readonly PendingSend[]): void {
  entries = next;
  for (const listener of [...listeners]) listener();
}

function patch(clientMessageId: string, update: Partial<PendingSend>): void {
  if (!entries.some((e) => e.clientMessageId === clientMessageId)) return;
  commit(entries.map((e) => (e.clientMessageId === clientMessageId ? { ...e, ...update } : e)));
}

function remove(clientMessageId: string): void {
  runs.delete(clientMessageId);
  const entry = entries.find((e) => e.clientMessageId === clientMessageId);
  if (!entry) return;
  commit(entries.filter((e) => e.clientMessageId !== clientMessageId));
  revokePendingPreviews(entry.previews);
}

function schedule(scope: string, clientMessageId: string): void {
  const previous = queues.get(scope) ?? Promise.resolve();
  const next = previous.then(async () => {
    const run = runs.get(clientMessageId);
    // Discarded while waiting its turn.
    if (!run) return;
    let outcome: PendingSendOutcome;
    try {
      outcome = await run();
    } catch {
      outcome = { ok: false };
    }
    if (outcome.ok) remove(clientMessageId);
    else patch(clientMessageId, { status: 'failed', error: outcome.error });
  });
  queues.set(scope, next);
  void next.finally(() => {
    if (queues.get(scope) === next) queues.delete(scope);
  });
}

/** Shows `entry` right away and queues `run` behind earlier sends in its scope. */
export function enqueuePendingSend(
  entry: Omit<PendingSend, 'status' | 'createdAt' | 'error'> & { createdAt?: string },
  run: Run,
): void {
  if (runs.has(entry.clientMessageId)) return;
  runs.set(entry.clientMessageId, run);
  commit([...entries, { ...entry, createdAt: entry.createdAt ?? new Date().toISOString(), status: 'sending' }]);
  schedule(entry.scope, entry.clientMessageId);
}

/** Re-queues a failed send with the same `clientMessageId`. */
export function retryPendingSend(clientMessageId: string): void {
  const entry = entries.find((e) => e.clientMessageId === clientMessageId);
  if (!entry || entry.status !== 'failed' || !runs.has(clientMessageId)) return;
  patch(clientMessageId, { status: 'sending', error: undefined });
  schedule(entry.scope, clientMessageId);
}

/** Drops a failed send. A send already in flight cannot be recalled. */
export function discardPendingSend(clientMessageId: string): void {
  const entry = entries.find((e) => e.clientMessageId === clientMessageId);
  if (!entry || entry.status !== 'failed') return;
  remove(clientMessageId);
}

export function getPendingSends(): readonly PendingSend[] {
  return entries;
}

export function subscribePendingSends(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Pending sends to render for one stream: the scope's own (by this identity),
 * minus any whose server copy is already present.
 */
export function selectPendingSends(
  all: readonly PendingSend[],
  params: {
    scope: string;
    identityId: string | undefined;
    /** Thread root for a thread panel; omit for the main stream. */
    threadRootId?: string;
    deliveredClientMessageIds: ReadonlySet<string>;
  },
): PendingSend[] {
  const { scope, identityId, threadRootId, deliveredClientMessageIds } = params;
  if (!identityId) return [];
  return all.filter(
    (e) =>
      e.scope === scope &&
      e.fromIdentityId === identityId &&
      (threadRootId ? e.threadRootId === threadRootId : e.inStream) &&
      !deliveredClientMessageIds.has(e.clientMessageId),
  );
}

/**
 * Drops every pending send (logout, identity switch, local wipe): rows hold
 * plaintext. A send already in flight still completes; queued ones never start.
 */
export function clearPendingSends(): void {
  runs.clear();
  if (entries.length === 0) return;
  const dropped = entries;
  commit([]);
  for (const e of dropped) revokePendingPreviews(e.previews);
}

/** Test-only: clears entries, queues, and subscribers. */
export function resetPendingSendsForTests(): void {
  entries = [];
  runs.clear();
  queues.clear();
  listeners.clear();
}
